import Mousetrap from 'mousetrap'
import { ShortcutLayer } from './constants'

/** 单字母快捷键延迟执行毫秒数，避免扫码枪首字符误触发（如 wxp:// 的 p） */
const SCAN_SAFE_DELAY = 120
/** 判定「连续按键 burst」的时间窗口 */
const SCAN_BURST_WINDOW_MS = 450
/** 窗口内达到该次数则视为扫码枪 burst，取消待执行的字母快捷键 */
const SCAN_BURST_MIN_KEYS = 2
/** 扫码确认后压制 Enter 快捷键的时长，防止扫码收尾 Enter 触发弹窗确定/结账 */
const SCAN_ENTER_SUPPRESS_MS = 80

/** 图层优先级，数字越大越靠栈顶：app < page < dialog < widget */
const LAYER_RANK = {
  [ShortcutLayer.APP]: 0,
  [ShortcutLayer.PAGE]: 1,
  [ShortcutLayer.DIALOG]: 2,
  [ShortcutLayer.WIDGET]: 3,
}

/**
 * 快捷键上下文中心。
 *
 * 同一时刻只有栈顶上下文能消费命令键，用来解决多页面/弹窗抢快捷键。
 * 扫码由 ScannerCore（捕获阶段 + dwell）识别，经 scannerBridge 交给 getScanSink()。
 *
 * 图层排序（LAYER_RANK）：app < page < dialog < widget。
 * FastAdd 固定在 widget 层且长期 onScan——这是历史设计，不能假设「栈顶 = 当前交互焦点」。
 * 按键分发与扫码路由 therefore 各有一套 override 规则，见 _dispatch / getScanSink。
 *
 * 分发时若 scannerBridge.scanBurstActive 为真，全部快捷键让开（组长方案避让）。
 *
 * 业务侧请通过 mixin `useShortcuts` 的 bindShortcuts / unbindShortcuts 使用，不要直接 new。
 */
export class ShortcutManager {
  constructor() {
    /** @type {boolean} false 时启动扫码桥（ScannerCore） */
    this.legacyMode = false
    /** @type {Map<string, object>} scopeId -> 上下文（keymap、onScan、图层） */
    this.scopes = new Map()
    /** @type {string[]} 激活中的 scopeId，末尾为栈顶 */
    this.stack = []
    /** @type {import('mousetrap').Mousetrap | null} 全中心共用的唯一 Mousetrap 实例 */
    this._trap = null
    /** @type {string[]} 当前已绑定到 Mousetrap 的键名 */
    this._boundKeys = []
    /** @type {Function | null} 捕获阶段记录按键间隔，用于识别扫码 burst */
    this._burstHandler = null
    /** @type {number[]} burst 窗口内的 keydown 时间戳 */
    this._recentKeydownTimes = []
    /** @type {ReturnType<typeof setTimeout> | null} 字母快捷键延迟定时器 */
    this._pendingShortcutTimer = null
    /** @type {Function | null} 等待执行的字母快捷键 */
    this._pendingShortcutFn = null
    /** @type {number} 此时间戳之前忽略 Enter 快捷键 */
    this._suppressEnterUntil = 0
    /** @type {import('./scannerBridge').ScannerBridge | null} 扫码桥，由 index.js 注入 */
    this.scannerBridge = null
    /** @type {string | null} 上次打印的激活接收者签名，避免重复刷屏 */
    this._lastActiveReceiverSignature = null
  }

  /**
   * 取当前栈顶上下文 id。
   * @returns {string | null}
   */
  getTopId() {
    return this.stack.length ? this.stack[this.stack.length - 1] : null
  }

  /**
   * 取当前栈顶上下文对象（含 keys、onScan、layer）。
   * @returns {object | null}
   */
  getTopScope() {
    return this.scopes.get(this.getTopId()) || null
  }

  /**
   * 判断某个上下文是否为栈顶（只有栈顶会真正响应按键）。
   * @param {string} scopeId
   * @returns {boolean}
   */
  isActive(scopeId) {
    return this.getTopId() === scopeId
  }

  /**
   * 登记一个上下文。页面/弹窗 bindShortcuts 时调用。
   * 同一 id 重复登记会合并 keys / onScan，不会丢掉已有绑定。
   *
   * @param {object} options
   * @param {string} options.id 唯一 id，一般是 `scope-${vm._uid}`
   * @param {string} [options.layer=page] app | page | dialog | widget
   * @param {Object<string, Function>} [options.keys] 键名 -> handler，如 `{ f4: this.handleSave }`
   * @param {Function} [options.onScan] 扫码枪确认后的接收口 `(code, ifScanGun) => {}`
   * @param {boolean} [options.exclusive] 是否挡住下层；dialog 默认 true
   * @param {boolean} [options.enterInInput] 焦点在 INPUT/TEXTAREA 时仍响应 enter（如备注框回车确认）
   * @param {Vue} [options.vm] handler 的 this
   * @returns {object | null} 登记后的 scope
   */
  register(options) {
    const {
      id,
      layer = ShortcutLayer.PAGE,
      keys = {},
      onScan = null,
      exclusive = null,
      enterInInput = null,
      vm = null,
      strictDigitBuffer = null,
      onDigitReplay = null,
    } = options || {}
    if (!id) {
      console.warn('TIPS shortcut register 缺少 id')
      return null
    }

    const nextExclusive = exclusive == null ? layer === ShortcutLayer.DIALOG : exclusive
    const prev = this.scopes.get(id)
    const scope = {
      id,
      layer,
      keys: { ...(prev?.keys || {}), ...keys },
      onScan: onScan != null ? onScan : prev?.onScan || null,
      exclusive: nextExclusive,
      enterInInput: enterInInput != null ? !!enterInInput : !!prev?.enterInInput,
      vm: vm || prev?.vm || null,
      enabled: prev ? prev.enabled : false,
      strictDigitBuffer:
        strictDigitBuffer != null ? !!strictDigitBuffer : !!prev?.strictDigitBuffer,
      onDigitReplay:
        onDigitReplay != null ? onDigitReplay : prev?.onDigitReplay || null,
    }
    this.scopes.set(id, scope)
    this._warnConflicts(scope)
    if (scope.enabled) this._refreshBindings()
    return scope
  }

  /**
   * 激活上下文，使其进入栈并开始响应按键。
   * - page：同时只允许一个，会关掉其它 page
   * - dialog / widget：压到栈顶，盖住下面的页面键
   * keep-alive 的 activated、弹窗打开时调用。
   *
   * @param {string} scopeId
   */
  activate(scopeId) {
    const scope = this.scopes.get(scopeId)
    if (!scope) return

    if (scope.layer === ShortcutLayer.PAGE) {
      this.stack.forEach((id) => {
        const other = this.scopes.get(id)
        if (other?.layer === ShortcutLayer.PAGE && id !== scopeId) {
          other.enabled = false
        }
      })
      this.stack = this.stack.filter((id) => this.scopes.get(id)?.layer !== ShortcutLayer.PAGE)
    } else {
      this.stack = this.stack.filter((id) => id !== scopeId)
    }

    scope.enabled = true
    this.stack.push(scopeId)
    this._sortStack()
    this._refreshBindings()
    this._logActiveReceiverChange('activate', scopeId)
  }

  /**
   * 停用上下文：移出栈，但登记还在，keep-alive 再进来可以 activate 恢复。
   * 对应 deactivated、弹窗关闭。
   *
   * @param {string} scopeId
   */
  deactivate(scopeId) {
    const scope = this.scopes.get(scopeId)
    if (!scope) return
    scope.enabled = false
    this.stack = this.stack.filter((id) => id !== scopeId)
    this._refreshBindings()
    this._logActiveReceiverChange('deactivate', scopeId)
  }

  /**
   * 彻底注销：停用并删除登记。组件 beforeDestroy / unbindShortcuts 时调用。
   *
   * @param {string} scopeId
   */
  unregister(scopeId) {
    this.deactivate(scopeId)
    this.scopes.delete(scopeId)
    this._refreshBindings()
  }

  /**
   * 单独更新某上下文的扫码接收口，不必重新 bind 整份 keymap。
   *
   * @param {string} scopeId
   * @param {Function | null} onScan
   */
  setOnScan(scopeId, onScan) {
    const scope = this.scopes.get(scopeId)
    if (!scope) return
    scope.onScan = onScan
    if (scope.enabled) this._syncStrictDigitMode()
  }

  /**
   * 仅关闭扫码付模式，保留 scope 内已绑定的页面/弹窗快捷键。
   * 用于新结算页切换支付方式时，避免 unregister 整页注销。
   *
   * @param {string} scopeId
   * @param {Object} [options]
   * @param {string} [options.layer] 恢复 layer，如切回 page
   */
  clearScanPayMode(scopeId, options = {}) {
    const scope = this.scopes.get(scopeId)
    if (!scope) return
    scope.onScan = null
    scope.strictDigitBuffer = false
    scope.onDigitReplay = null
    if (options.layer) scope.layer = options.layer
    if (scope.enabled) {
      this._refreshBindings()
      this._syncStrictDigitMode()
    }
  }

  /**
   * 从栈顶往下找扫码接收口，供 scannerBridge 回调。
   *
   * POS 奇葩点：FastAdd 在 widget 层且常驻 onScan，排序永远压在 dialog 之上；
   * 若只按栈顶找 onScan，弹窗打开时扫码仍会落回主搜索框。
   *
   * 规则（分两轮，不可合并为一轮）：
   * 1. 栈顶→下：遇到 enabled 的 exclusive dialog → 有 onScan 则消费，无则 null（全挡，含 widget）
   * 2. 无 exclusive dialog 挡路时，再找 widget/page 的 onScan
   *
   * @returns {Function | null}
   */
  getScanSink() {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scope = this.scopes.get(this.stack[i])
      if (!scope || !scope.enabled) continue
      if (scope.layer === ShortcutLayer.DIALOG && scope.exclusive) {
        return typeof scope.onScan === 'function' ? scope.onScan : null
      }
    }
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scope = this.scopes.get(this.stack[i])
      if (!scope || !scope.enabled) continue
      if (typeof scope.onScan === 'function') return scope.onScan
    }
    return null
  }

  /**
   * 扫码枪已确认一笔码：短时间内忽略 Enter 快捷键，并取消待执行的字母快捷键。
   * 由 scannerBridge 在判定 ifScanGun 后调用。
   */
  markScanEnterConsumed() {
    this._suppressEnterUntil = Date.now() + SCAN_ENTER_SUPPRESS_MS
    this._cancelPendingLetterShortcut()
  }

  /**
   * 调试用：当前栈、各 scope 绑定了哪些键。可在控制台 this.$shortcuts.getDebugState()。
   *
   * @returns {object}
   */
  getDebugState() {
    return {
      legacyMode: this.legacyMode,
      scanBurstActive: !!this.scannerBridge?.scanBurstActive,
      scanner: this.scannerBridge?.getDebugState?.() || null,
      stack: this.stack.slice(),
      stackTop: this._describeScope(this.getTopScope()),
      keyReceiver: this._describeScope(this._getKeyReceiverScope()),
      scanReceiver: this._describeScope(this._getScanReceiverScope()),
      scopes: [...this.scopes.values()].map((s) => ({
        id: s.id,
        layer: s.layer,
        enabled: s.enabled,
        exclusive: !!s.exclusive,
        component: s.vm?.$options?.name || null,
        keys: Object.keys(s.keys),
        hasOnScan: typeof s.onScan === 'function',
        strictDigitBuffer: !!s.strictDigitBuffer,
      })),
    }
  }

  /**
   * @private
   * @param {object | null} scope
   * @returns {object | null}
   */
  _describeScope(scope) {
    if (!scope) return null
    return {
      id: scope.id,
      layer: scope.layer,
      exclusive: !!scope.exclusive,
      component: scope.vm?.$options?.name || null,
      hasOnScan: typeof scope.onScan === 'function',
    }
  }

  /**
   * 命令键实际焦点：优先 exclusive dialog（即使 widget 在栈顶）；否则取栈顶 enabled。
   * @private
   * @returns {object | null}
   */
  _getKeyReceiverScope() {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scope = this.scopes.get(this.stack[i])
      if (!scope || !scope.enabled) continue
      if (scope.layer === ShortcutLayer.DIALOG && scope.exclusive) return scope
    }
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scope = this.scopes.get(this.stack[i])
      if (scope?.enabled) return scope
    }
    return null
  }

  /**
   * 扫码实际接收 scope（与 getScanSink 规则一致）。
   * @private
   * @returns {object | null}
   */
  _getScanReceiverScope() {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scope = this.scopes.get(this.stack[i])
      if (!scope || !scope.enabled) continue
      if (scope.layer === ShortcutLayer.DIALOG && scope.exclusive) {
        return typeof scope.onScan === 'function' ? scope : null
      }
    }
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scope = this.scopes.get(this.stack[i])
      if (!scope || !scope.enabled) continue
      if (typeof scope.onScan === 'function') return scope
    }
    return null
  }

  /**
   * 栈变化后打印当前激活接收者（仅签名变化时打印）。
   * @private
   * @param {'activate'|'deactivate'} reason
   * @param {string} [changedScopeId]
   */
  _logActiveReceiverChange(reason, changedScopeId) {
    const stackTop = this._describeScope(this.getTopScope())
    const keyReceiver = this._describeScope(this._getKeyReceiverScope())
    const scanReceiver = this._describeScope(this._getScanReceiverScope())
    const signature = [
      stackTop?.id || '-',
      keyReceiver?.id || '-',
      scanReceiver?.id || '-',
      this.stack.join('>'),
    ].join('|')
    if (signature === this._lastActiveReceiverSignature) return
    this._lastActiveReceiverSignature = signature
    console.log('TIPS-key shortcut-active', {
      reason,
      changed: changedScopeId || null,
      stackTop,
      keyReceiver,
      scanReceiver,
      stack: this.stack.slice(),
    })
  }

  /**
   * 按图层优先级重排栈，保证 widget > dialog > page > app。
   * @private
   */
  _sortStack() {
    this.stack.sort((a, b) => {
      const ra = LAYER_RANK[this.scopes.get(a)?.layer] || 0
      const rb = LAYER_RANK[this.scopes.get(b)?.layer] || 0
      return ra - rb
    })
  }

  /**
   * 同层两个启用中的 scope 抢同一按键时打冲突日志，便于迁页时排查。
   * @private
   * @param {object} scope
   */
  _warnConflicts(scope) {
    Object.keys(scope.keys).forEach((key) => {
      this.scopes.forEach((other) => {
        if (other.id === scope.id) return
        if (other.layer !== scope.layer) return
        if (!other.enabled && !scope.enabled) return
        if (other.keys[key]) {
          console.warn(`TIPS shortcut conflict: ${key} ${scope.layer}:${scope.id} vs ${other.layer}:${other.id}`)
        }
      })
    })
  }

  /**
   * 懒创建唯一的 Mousetrap 实例。关掉默认「输入框内不触发」，改由 _shouldBlockInInput 判断。
   * @private
   */
  _ensureTrap() {
    if (this._trap) return
    this._trap = Mousetrap()
    this._trap.stopCallback = () => false
  }

  /**
   * 按当前所有「已激活」scope 重建 Mousetrap 绑定。
   * 没有启用中的 scope 时销毁实例，避免空监听占着 document。
   * @private
   */
  _refreshBindings() {
    const enabledScopes = [...this.scopes.values()].filter((s) => s.enabled)
    if (!enabledScopes.length) {
      this._unbindAll()
      if (this._trap) {
        this._trap.reset()
        if (typeof this._trap.destroy === 'function') this._trap.destroy()
        this._trap = null
      }
      this._uninstallBurstListener()
      this._syncStrictDigitMode()
      return
    }

    this._ensureTrap()
    this._unbindAll()

    const keys = new Set()
    let needBurst = false
    enabledScopes.forEach((scope) => {
      Object.keys(scope.keys).forEach((key) => {
        keys.add(key)
        if (this._isLetterKey(key)) needBurst = true
      })
    })

    this._boundKeys = [...keys]
    if (this._boundKeys.length) {
      this._trap.bind(this._boundKeys, (e, combo) => this._dispatch(combo, e))
    }

    if (needBurst) this._installBurstListener()
    else this._uninstallBurstListener()

    this._syncStrictDigitMode()
  }

  /**
   * 栈顶若声明付款严格数字缓冲，则打开 ScannerCore 严格模式。
   * 数字从首键吞掉，人手回放走 scope.onDigitReplay → handleSetNumber。
   * @private
   */
  _syncStrictDigitMode() {
    const top = this.getTopScope()
    const enabled = !!(
      top?.enabled &&
      top.strictDigitBuffer &&
      typeof top.onDigitReplay === 'function'
    )
    this.scannerBridge?.setStrictDigitMode?.(enabled, enabled ? (chars) => this._replayPayDigits(chars) : null)
  }

  /**
   * 人手金额数字回放（严格模式）
   * @private
   * @param {string} chars
   */
  _replayPayDigits(chars) {
    const top = this.getTopScope()
    if (!top?.enabled || typeof top.onDigitReplay !== 'function') return
    top.onDigitReplay.call(top.vm || null, chars)
  }

  /**
   * 解掉当前 Mousetrap 上的全部键，不清实例。
   * @private
   */
  _unbindAll() {
    if (this._trap && this._boundKeys.length) {
      this._trap.unbind(this._boundKeys)
    }
    this._boundKeys = []
  }

  /**
   * 按键分发入口：从栈顶往下找认领该键的 handler。
   * - 扫码 burst 进行中：全部快捷键让开（含数字/字母/Space），避免吞码、污染金额、误结账
   * - dialog exclusive：即使自己没绑这个键，也挡住页面
   * - 单字母：延迟执行，兜住 burst 首字符尚未判定的一拍
   * 返回 true 表示不 preventDefault，把字符留给输入框/扫码枪。
   *
   * @private
   * @param {string} combo Mousetrap 组合名，如 f4、esc、p
   * @param {KeyboardEvent} e
   * @returns {boolean}
   */
  _dispatch(combo, e) {
    const key = String(combo || '').toLowerCase()

    // 组长方案：扫码 burst 中快捷键整体避让
    if (this.scannerBridge?.scanBurstActive) {
      this._cancelPendingLetterShortcut()
      return true
    }

    if (key === 'enter' && Date.now() < this._suppressEnterUntil) {
      return true
    }

    if (this._isLetterKey(key) && this._shouldBlockInInput(e)) {
      return true
    }
    if (key === 'space' && this._shouldBlockInInput(e)) {
      return true
    }
    // 备注等输入框内：数字/小数点/退格/回车留给原生，不走金额快捷键
    // 否则 debounce(handleSetNumber) 会在扫码回车失焦后把末位写进金额
    if (this._isAmountInputKey(key) && this._shouldBlockInInput(e)) {
      return true
    }
    if (key === 'enter' && this._shouldBlockInInput(e)) {
      if (!this._getEnterInInputScope()) {
        return true
      }
    }

    const enterInInputScope = key === 'enter' && this._shouldBlockInInput(e) ? this._getEnterInInputScope() : null

    const run = () => {
      for (let i = this.stack.length - 1; i >= 0; i--) {
        const scope = this.scopes.get(this.stack[i])
        if (!scope || !scope.enabled) continue
        const handler = scope.keys[combo] || scope.keys[key] || scope.keys[this._normalizeKey(key)]
        if (typeof handler === 'function') {
          console.log('TIPS-key shortcut-center', {
            key: combo,
            scopeId: scope.id,
            layer: scope.layer,
            component: scope.vm?.$options?.name,
          })
          const result = handler.call(scope.vm, e, combo)
          if (scope.exclusive) return result
          if (result === false) return false
          continue
        }
        if (scope.exclusive && scope.layer === ShortcutLayer.DIALOG) {
          return true
        }
      }
      return true
    }

    if (this._isLetterKey(key)) {
      this._scheduleLetterShortcut(run)
      return true
    }

    const result = run()
    if (enterInInputScope) {
      return false
    }
    return result
  }

  /**
   * 延迟执行字母快捷键；期间若又有按键则取消（扫码 burst）。
   * @private
   * @param {Function} fn
   */
  _scheduleLetterShortcut(fn) {
    this._cancelPendingLetterShortcut()
    this._pendingShortcutFn = fn
    this._pendingShortcutTimer = setTimeout(() => {
      const pending = this._pendingShortcutFn
      this._pendingShortcutTimer = null
      this._pendingShortcutFn = null
      // 延迟落地时若已进入扫码 burst，或旧间隔 burst，都不执行
      if (this.scannerBridge?.scanBurstActive || this._isRapidKeyBurst()) return
      if (typeof pending === 'function') pending()
    }, SCAN_SAFE_DELAY)
  }

  /**
   * 取消尚未执行的字母快捷键。
   * @private
   */
  _cancelPendingLetterShortcut() {
    if (this._pendingShortcutTimer) {
      clearTimeout(this._pendingShortcutTimer)
      this._pendingShortcutTimer = null
      this._pendingShortcutFn = null
    }
  }

  /**
   * 挂捕获阶段 keydown，记录按键间隔，用于识别扫码枪连续输入。
   * @private
   */
  _installBurstListener() {
    if (this._burstHandler) return
    this._burstHandler = () => {
      const now = Date.now()
      this._recentKeydownTimes.push(now)
      this._recentKeydownTimes = this._recentKeydownTimes.filter((t) => now - t <= SCAN_BURST_WINDOW_MS)
      if (this._pendingShortcutTimer) this._cancelPendingLetterShortcut()
    }
    window.addEventListener('keydown', this._burstHandler, true)
  }

  /**
   * 卸掉 burst 监听并清空待执行字母快捷键。
   * @private
   */
  _uninstallBurstListener() {
    this._cancelPendingLetterShortcut()
    this._recentKeydownTimes = []
    if (this._burstHandler) {
      window.removeEventListener('keydown', this._burstHandler, true)
      this._burstHandler = null
    }
  }

  /**
   * 最近是否像扫码枪连敲（短时间多次 keydown）。
   * @private
   * @returns {boolean}
   */
  _isRapidKeyBurst() {
    return this._recentKeydownTimes.length >= SCAN_BURST_MIN_KEYS
  }

  /**
   * Mousetrap combo 与 KeyboardEvent.key 别名对齐
   * @private
   * @param {string} key
   * @returns {string}
   */
  _normalizeKey(key) {
    const map = {
      arrowup: 'up',
      arrowdown: 'down',
      arrowleft: 'left',
      arrowright: 'right',
    }
    return map[String(key || '').toLowerCase()] || key
  }

  /**
   * 是否单字母键（a-z）。这类键要延迟，F1/Esc 等立即执行。
   * @private
   * @param {string} key
   * @returns {boolean}
   */
  _isLetterKey(key) {
    return /^[a-z]$/.test(String(key || '').toLowerCase())
  }

  /**
   * 金额相关快捷键（付款面板 0-9 / . / backspace）
   * @private
   * @param {string} key
   * @returns {boolean}
   */
  _isAmountInputKey(key) {
    return /^[0-9]$/.test(key) || key === '.' || key === 'backspace'
  }

  /**
   * 栈顶往下找声明了 enterInInput 且绑了 enter 的 scope。
   * @private
   * @returns {object | null}
   */
  _getEnterInInputScope() {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scope = this.scopes.get(this.stack[i])
      if (!scope || !scope.enabled) continue
      const handler = scope.keys.enter || scope.keys.Enter
      if (scope.enterInInput && typeof handler === 'function') {
        return scope
      }
      if (scope.exclusive && scope.layer === ShortcutLayer.DIALOG) {
        return null
      }
    }
    return null
  }

  /**
   * 焦点在普通输入框时，不把单字母 / Space 当快捷键（避免备注框里按 p 删行）。
   * 数量框等需要全局快捷键的，加 data-shortcut="allow" 或 data-class="mousetrap"。
   *
   * @private
   * @param {KeyboardEvent} e
   * @returns {boolean} true 表示应拦截、不触发快捷键
   */
  _shouldBlockInInput(e) {
    const el = e?.target || document.activeElement
    if (!el?.tagName) return false
    const tag = el.tagName
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT' && !el.isContentEditable) {
      return false
    }
    if (el.getAttribute?.('data-shortcut') === 'allow') return false
    if (el.getAttribute?.('data-class')?.includes('mousetrap')) return false
    if (el.classList?.contains?.('mousetrap')) return false
    return true
  }
}

export default ShortcutManager
